import React, { memo } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import classNames from "classnames";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { ApplicationView, useApplicationView } from "@hooks/useApplicationView";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { Button } from "@design-system/Button/Button";
import { HEADER_HEIGHT } from "@constants";
import { styleWithVars } from "@lib/cssVars";
import { CogwheelIcon, PlusIcon } from "@icons";
import { useOptionalSideNavContext } from "@design-system/SideNav/SideNavProvider";
import { SideNavShowButton } from "@design-system/SideNav/SideNavShowButton";
import { useSettings } from "@components/settings/SettingsProvider";
import { useCommands } from "../commands/useCommands";
import { LazyChunkBoundary } from "@design-system/LazyChunkBoundary/LazyChunkBoundary";
import { UserDropdown } from "./UserDropdown/UserDropdown";
import { type Breadcrumb, HeaderBreadcrumbs } from "./HeaderBreadcrumbs";
import styles from "./MainInterface.module.scss";

export type { Breadcrumb } from "./HeaderBreadcrumbs";

const ChatButton = React.lazy(() =>
    import("./chat/ChatButton").then((m) => ({ default: m.ChatButton })),
);

const BackgroundJobsIndicator = React.lazy(() =>
    import("./BackgroundJobsIndicator").then((m) => ({
        default: m.BackgroundJobsIndicator,
    })),
);

const RevertProgressTracker = React.lazy(() =>
    import("../history/components/RevertProgressTracker").then((m) => ({
        default: m.RevertProgressTracker,
    })),
);

const ScheduleRuleOutcomeTracker = React.lazy(() =>
    import("../transactionRules/ScheduleRuleOutcome/ScheduleRuleOutcomeTracker").then(
        (m) => ({
            default: m.ScheduleRuleOutcomeTracker,
        }),
    ),
);

interface Props {
    breadcrumbs?: Breadcrumb[];
    dropdownItems?: React.ReactNode;
}

export const MainInterfaceHeader: React.FC<Props> = memo(
    ({ breadcrumbs = [], dropdownItems }) => {
        const sideNav = useOptionalSideNavContext();
        const { open: openCommands } = useCommands();
        const { open: openSettings } = useSettings();
        const { backgroundJobIndicator, closeManagement } = useTypedFlags();
        const applicationView = useApplicationView();
        // PERSONAL_VIEW has no chat scope, so its panel would open on an
        // endless spinner. See PRO-10947.
        const shouldShowChatButton =
            applicationView === ApplicationView.MAIN ||
            (closeManagement &&
                applicationView === ApplicationView.ORGANIZATION_VIEW);
        const shouldShowBackgroundJobsIndicator = backgroundJobIndicator;

        useKeyboardCommand({
            keys: "mod+b",
            callback: () => {
                sideNav?.toggle();
            },
            options: {
                description: "Toggle navigation",
            },
        });

        return (
            <header className={styles.header}>
                <div
                    className={classNames(
                        styles.container,
                        styles.headerContainer,
                    )}
                    style={styleWithVars({ "--height": `${HEADER_HEIGHT}px` })}
                >
                    <aside className={styles.headerContent}>
                        <div className={styles.headerBreadcrumbs}>
                            {sideNav && <SideNavShowButton className="mr-1" />}
                            <HeaderBreadcrumbs breadcrumbs={breadcrumbs} />
                        </div>

                        <div className={styles.headerActions}>
                            <LazyChunkBoundary>
                                <RevertProgressTracker />
                            </LazyChunkBoundary>
                            <LazyChunkBoundary>
                                <ScheduleRuleOutcomeTracker />
                            </LazyChunkBoundary>
                            {shouldShowBackgroundJobsIndicator && (
                                <LazyChunkBoundary>
                                    <BackgroundJobsIndicator />
                                </LazyChunkBoundary>
                            )}

                            {!sideNav && (
                                <Tooltip
                                    content="Open actions modal"
                                    shortcut="mod+k"
                                    side="left"
                                >
                                    <Button
                                        variant="primary"
                                        size="md"
                                        iconOnly
                                        onClick={() => openCommands()}
                                        aria-label="Open actions modal"
                                    >
                                        <PlusIcon className="icon-color-gray-900" />
                                    </Button>
                                </Tooltip>
                            )}

                            <Tooltip content="Settings" side="left">
                                <Button
                                    variant="primary"
                                    size="md"
                                    iconOnly
                                    onClick={() => openSettings()}
                                    aria-label="Open settings"
                                >
                                    <CogwheelIcon
                                        aria-hidden="true"
                                        className="icon-color-gray-900"
                                    />
                                </Button>
                            </Tooltip>

                            {shouldShowChatButton && (
                                <LazyChunkBoundary>
                                    <ChatButton />
                                </LazyChunkBoundary>
                            )}

                            <UserDropdown items={dropdownItems} />
                        </div>
                    </aside>
                </div>
            </header>
        );
    },
);

MainInterfaceHeader.displayName = "MainInterfaceHeader";
